<template>
  <v-card class="c3-filter-bar" flat>
    <div class="c3-filter-bar__row">
      <template v-for="f in layout">
        <!-- Search, optionally scoped ("Buscar en") -->
        <div v-if="f === 'search'" :key="'search'" class="c3-filter-bar__search">
        <v-select
          v-if="searchScopes.length"
          :value="value.searchScope"
          :items="searchScopes"
          item-text="text"
          item-value="value"
          class="c3-filter-bar__scope"
          outlined
          dense
          hide-details
          background-color="#FAFAFA"
          @change="update('searchScope', $event)"
        />
        <v-text-field
          :value="value.search"
          :placeholder="searchPlaceholder"
          outlined
          dense
          hide-details
          clearable
          background-color="#FAFAFA"
          append-icon="mdi-magnify"
          @input="update('search', $event || '')"
        />
        </div>

        <div v-else :key="f.key" class="c3-filter-bar__field">
        <v-select
          v-if="f.type === 'select'"
          :value="value[f.key]"
          :items="f.items"
          :item-text="f.itemText || 'text'"
          :item-value="f.itemValue || 'value'"
          :multiple="f.multiple"
          :placeholder="f.label"
          :clearable="!('defaultValue' in f)"
          :return-object="f.returnObject"
          outlined
          dense
          hide-details
          background-color="#FAFAFA"
          no-data-text="Sin opciones"
          @change="update(f.key, $event)"
        >
          <template v-if="f.multiple" v-slot:selection="{ item, index }">
            <span v-if="index === 0" class="c3-filter-bar__sel">{{ itemText(f, item) }}</span>
            <span v-if="index === 1" class="grey--text text-caption">
              &nbsp;(+{{ value[f.key].length - 1 }})
            </span>
          </template>
        </v-select>

        <v-menu
          v-else-if="f.type === 'dates'"
          v-model="menus[f.key]"
          :close-on-content-click="false"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="datesLabel(value[f.key])"
              :placeholder="f.label"
              outlined
              dense
              hide-details
              readonly
              clearable
              background-color="#FAFAFA"
              append-icon="mdi-calendar-blank"
              v-bind="attrs"
              v-on="on"
              @click:clear="update(f.key, [])"
            />
          </template>
          <v-card class="c3-filter-bar__dates">
            <div class="c3-filter-bar__shortcuts">
              <v-btn
                v-for="s in dateShortcuts"
                :key="s.text"
                text
                small
                @click="pickShortcut(f.key, s)"
              >{{ s.text }}</v-btn>
            </div>
            <v-date-picker
              :value="pending[f.key] || value[f.key] || []"
              range
              no-title
              locale="es"
              color="primary"
              :max="today"
              @input="pickRange(f.key, $event)"
            />
          </v-card>
        </v-menu>

        <v-checkbox
          v-else-if="f.type === 'checkbox'"
          :input-value="!!value[f.key]"
          :label="f.label"
          class="c3-filter-bar__checkbox mt-0 pt-0"
          color="#ffce00"
          hide-details
          @change="update(f.key, !!$event)"
        />

        <v-chip
          v-else-if="f.type === 'toggle'"
          :input-value="!!value[f.key]"
          filter
          outlined
          class="c3-filter-bar__toggle"
          @click="update(f.key, !value[f.key])"
        >{{ f.label }}</v-chip>
        </div>
      </template>


      <v-spacer />
      <div v-if="$slots.actions" class="c3-filter-bar__actions">
        <slot name="actions" />
      </div>
    </div>

  </v-card>
</template>

<script>
// Shared filter bar used by every list screen, so filters look and behave
// the same everywhere: search (optionally scoped), selects (single or
// multiple), a date range with shortcuts, toggles and checkboxes.
//
// v-model is a plain object ({ search, searchScope, <field.key>: value }).
// Each field: { key, type: 'select' | 'dates' | 'toggle', label, items,
// itemText, itemValue, multiple, returnObject, defaultValue, before }.
// A field with defaultValue can't be cleared (it always has a value);
// fields with before: true go before the search box.
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default {
  props: {
    value: { type: Object, required: true },
    fields: { type: Array, default: () => [] },
    searchScopes: { type: Array, default: () => [] },
    searchPlaceholder: { type: String, default: "Buscar..." },
  },
  data() {
    return { menus: {}, pending: {}, today: iso(new Date()) };
  },
  computed: {
    layout() {
      return [
        ...this.fields.filter((f) => f.before),
        "search",
        ...this.fields.filter((f) => !f.before),
      ];
    },
    dateShortcuts() {
      const now = new Date();
      const day = (now.getDay() + 6) % 7; // Monday = 0
      const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const firstPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastPrev = new Date(now.getFullYear(), now.getMonth(), 0);
      return [
        { text: "Hoy", range: [iso(now), iso(now)] },
        { text: "Esta semana", range: [iso(monday), iso(now)] },
        { text: "Este mes", range: [iso(firstOfMonth), iso(now)] },
        { text: "Mes pasado", range: [iso(firstPrev), iso(lastPrev)] },
      ];
    },
  },
  methods: {
    update(key, val) {
      this.$emit("input", { ...this.value, [key]: val });
    },
    itemText(f, item) {
      return typeof item === "object" ? item[f.itemText || "text"] : item;
    },
    datesLabel(v) {
      if (!v || !v.length) return "";
      const s = this.dateShortcuts.find((x) => x.range[0] === v[0] && x.range[1] === v[1]);
      if (s) return s.text;
      return v.length > 1 && v[1] !== v[0] ? `${v[0]} – ${v[1]}` : v[0];
    },
    // First click only marks the start; the range is applied on the second.
    pickRange(key, range) {
      if (range.length < 2) {
        this.$set(this.pending, key, range);
        return;
      }
      this.$set(this.pending, key, null);
      this.update(key, [...range].sort());
      this.$set(this.menus, key, false);
    },
    pickShortcut(key, s) {
      this.$set(this.pending, key, null);
      this.update(key, s.range);
      this.$set(this.menus, key, false);
    },
  },
};
</script>

<style scoped>
.c3-filter-bar {
  padding: 12px 16px;
  border-radius: 12px !important;
  background: transparent !important;
}
.c3-filter-bar__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}
.c3-filter-bar__search {
  display: flex;
  gap: 6px;
  flex: 1 1 320px;
  min-width: 300px;
  max-width: 440px;
}
.c3-filter-bar__scope {
  flex: 0 0 140px;
}
.c3-filter-bar__field {
  flex: 0 1 190px;
  min-width: 165px;
}
/* Same placeholder size as the original search fields. */
.c3-filter-bar ::v-deep input::placeholder,
.c3-filter-bar ::v-deep .v-select__selection {
  font-size: 12px;
}
/* Keep multi-selects one line tall, like the other fields. */
.c3-filter-bar__field ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}
.c3-filter-bar__field ::v-deep .v-select__selections input {
  min-width: 0;
  width: 0;
}
.c3-filter-bar__sel {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
}
.c3-filter-bar__checkbox ::v-deep label {
  white-space: nowrap;
  font-family: "Poppins", sans-serif;
  font-weight: bold;
  color: #000000;
}
.c3-filter-bar__field:has(.c3-filter-bar__checkbox) {
  flex: 0 0 auto;
}
.c3-filter-bar__toggle {
  height: 40px !important;
}
.c3-filter-bar__dates {
  display: flex;
}
.c3-filter-bar__shortcuts {
  display: flex;
  flex-direction: column;
  padding: 8px 4px;
  border-right: 1px solid #eee;
}
.c3-filter-bar__shortcuts .v-btn {
  justify-content: flex-start;
  text-transform: none;
}
.c3-filter-bar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  margin-left: auto;
}
</style>
